import $ from "jquery";

export const createCarousel = (element, { onCloneItems } = {}) => {
  const instance = {
    init(element) {
      // Initialize DOM elements
      this.$element = $(element);
      if (this.$element.data("initialized")) return;

      this.$itemsContainer = this.$element.find(".js-carousel-items");
      this.$items = this.$itemsContainer.find(".js-carousel-item");
      this.carouselId = this.$element.attr("id");

      // Get navigation buttons
      this.$prevButton = $(
        `.js-carousel-prev[data-carousel="${this.carouselId}"]`
      );
      this.$nextButton = $(
        `.js-carousel-next[data-carousel="${this.carouselId}"]`
      );

      // Initialize state
      this.isDragging = false;
      this.startPosition = 0;
      this.dragStartX = 0;
      this.dragStartTime = 0;
      this.dragThreshold = 5;
      this.hasMoved = false;

      this.onCloneItems = onCloneItems;

      // Set up carousel
      this.setupCarousel();
      this.bindEvents();

      this.$element.attr("data-initialized", true);
    },

    setupCarousel() {
      // Get initial measurements
      const originalItemCount = this.$items.length;
      const itemWidth = Math.round(this.$items.first().outerWidth());

      // Store important measurements
      this.itemWidth = itemWidth;
      this.totalWidth = itemWidth * originalItemCount;

      // Clone items for infinite scroll
      const firstClones = this.$items
        .clone(false)
        .addClass("carousel-clone first-clone");
      const lastClones = this.$items
        .clone(false)
        .addClass("carousel-clone last-clone");

      // Initialize clones if callback provided
      if (typeof this.onCloneItems === "function") {
        this.onCloneItems(firstClones);
        this.onCloneItems(lastClones);
      }

      // Add clones to DOM
      this.$itemsContainer.append(firstClones);
      this.$itemsContainer.prepend(lastClones);

      // Update items collection to include clones
      this.$items = this.$itemsContainer.find(".js-carousel-item");

      // Set initial position to show first real item
      this.currentPosition = -this.totalWidth;
      this.updatePosition(false);
    },

    bindEvents() {
      // Navigation buttons
      this.$prevButton.on("click", (e) => {
        e.preventDefault();
        this.slide("prev");
      });

      this.$nextButton.on("click", (e) => {
        e.preventDefault();
        this.slide("next");
      });

      // Separate touch and mouse handling
      if ("ontouchstart" in window) {
        // Touch devices
        this.$itemsContainer
          .on("touchstart", (e) => this.dragStart(e))
          .on("touchmove", (e) => this.drag(e))
          .on("touchend touchcancel", (e) => this.dragEnd(e));
      } else {
        // Mouse devices
        this.$itemsContainer
          .css("cursor", "grab")
          .on("mousedown", (e) => this.dragStart(e))
          .on("mousemove", (e) => this.drag(e))
          .on("mouseup mouseleave", (e) => this.dragEnd(e));
      }

      // Modify click prevention for better touch handling
      let touchMoved = false;
      this.$items
        .on("touchstart", () => {
          touchMoved = false;
        })
        .on("touchmove", () => {
          touchMoved = true;
        })
        .on("click", "a, button", (e) => {
          if (
            (this.hasMoved && Date.now() - this.dragStartTime < 300) ||
            touchMoved
          ) {
            e.preventDefault();
            e.stopPropagation();
          }
        });

      // Prevent image dragging
      this.$items.on("dragstart", (e) => e.preventDefault());
    },

    slide(direction) {
      if (this.isDragging) return;

      const moveAmount =
        direction === "next" ? this.itemWidth : -this.itemWidth;
      this.currentPosition = Math.round(this.currentPosition - moveAmount);
      this.updatePosition(true);
    },

    updatePosition(animate = true) {
      // Apply transform
      this.$itemsContainer.css({
        transition: animate ? "transform 300ms ease-in-out" : "none",
        transform: `translateX(${this.currentPosition}px)`,
      });

      // Check for loop points
      if (animate) {
        const rightEdge = -this.itemWidth;
        const leftEdge = -(this.totalWidth * 2);

        // Wait for animation to complete before resetting position
        if (
          this.currentPosition > rightEdge ||
          this.currentPosition < leftEdge
        ) {
          setTimeout(() => {
            if (this.currentPosition > rightEdge) {
              this.currentPosition -= this.totalWidth;
            } else if (this.currentPosition < leftEdge) {
              this.currentPosition += this.totalWidth;
            }
            this.updatePosition(false);
          }, 300); // Match the transition duration
        }
      }
    },

    dragStart(e) {
      // Don't prevent default on touch events to allow scrolling
      if (e.type !== "touchstart") {
        e.preventDefault();
      }

      this.isDragging = true;
      this.hasMoved = false;
      this.dragStartX = this.getPositionX(e);
      this.startPosition = this.currentPosition;
      this.dragStartTime = Date.now();

      if (e.type !== "touchstart") {
        this.$itemsContainer.css({
          cursor: "grabbing",
          transition: "none",
        });
      }
    },

    drag(e) {
      if (!this.isDragging) return;

      // Prevent page scrolling during drag on touch devices
      if (
        e.type === "touchmove" &&
        Math.abs(this.currentPosition - this.startPosition) > this.dragThreshold
      ) {
        e.preventDefault();
      }

      const currentX = this.getPositionX(e);
      const diff = currentX - this.dragStartX;

      if (Math.abs(diff) > this.dragThreshold) {
        this.hasMoved = true;
        this.$element.addClass("is-dragging");
      }

      this.currentPosition = Math.round(this.startPosition + diff);
      this.updatePosition(false);
    },

    dragEnd(e) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.$itemsContainer.css("cursor", "grab");
      this.$element.removeClass("is-dragging");

      // Don't reset hasMoved immediately - let the click handler use it
      // The click event (if any) will fire right after dragEnd

      // Snap to nearest item
      const moveBy = this.currentPosition % this.itemWidth;
      if (Math.abs(moveBy) > this.itemWidth / 3) {
        this.currentPosition -= this.itemWidth + moveBy;
      } else {
        this.currentPosition -= moveBy;
      }

      this.updatePosition(true);
    },

    getPositionX(e) {
      // Handle both touch and mouse events
      if (e.type.includes("touch")) {
        return e.touches[0]
          ? e.touches[0].clientX
          : e.changedTouches[0].clientX;
      }
      return e.pageX;
    },
  };

  instance.init(element);
  return instance;
};
